import React, { useEffect, useRef, useState } from 'react';
import { ScrollbarContainer, ContentWrapper, ScrollbarTrack, ScrollbarThumb } from './CustomScrollbar.styles';

interface CustomScrollbarProps {
  children: React.ReactNode;
  className?: string;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  scrollRef?: React.MutableRefObject<HTMLDivElement | null>;
}

export const CustomScrollbar: React.FC<CustomScrollbarProps> = ({ children, className, onScroll, scrollRef }) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [thumbHeight, setThumbHeight] = useState(50);
  const [scrollStartPosition, setScrollStartPosition] = useState<number | null>(null);
  const [initialScrollTop, setInitialScrollTop] = useState<number>(0);
  const [isDragging, setIsDragging] = useState(false);

  // Combine refs
  const combinedRef = (node: HTMLDivElement) => {
    if (scrollRef) {
      scrollRef.current = node;
    }
    contentRef.current = node;
  };

  // Calculate thumb height based on content height
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const updateThumbHeight = () => {
      const { scrollHeight, clientHeight } = content;
      const calculatedHeight = Math.max(
        50, // Minimum height
        (clientHeight / scrollHeight) * clientHeight,
      );
      setThumbHeight(calculatedHeight);
    };

    updateThumbHeight();

    const observer = new ResizeObserver(updateThumbHeight);
    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  // Handle scroll events
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const handleScroll = () => {
      if (!thumbRef.current || !trackRef.current || !content) return;

      const { scrollTop, scrollHeight } = content;
      const trackHeight = trackRef.current.clientHeight;
      const thumbPosition = (scrollTop / scrollHeight) * trackHeight;

      thumbRef.current.style.transform = `translateY(${thumbPosition}px)`;
    };

    content.addEventListener('scroll', handleScroll);
    return () => content.removeEventListener('scroll', handleScroll);
  }, []);

  // Mouse down on thumb
  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setScrollStartPosition(e.clientY);
    if (contentRef.current) {
      setInitialScrollTop(contentRef.current.scrollTop);
    }
    setIsDragging(true);
  };

  // Mouse move on document when dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !scrollStartPosition || !contentRef.current || !trackRef.current) return;

      const deltaY = e.clientY - scrollStartPosition;
      const trackHeight = trackRef.current.clientHeight;
      const scrollRatio = contentRef.current.scrollHeight / trackHeight;

      contentRef.current.scrollTop = initialScrollTop + deltaY * scrollRatio;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('mouseleave', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mouseleave', handleMouseUp);
    };
  }, [isDragging, scrollStartPosition, initialScrollTop]);

  // Click on track
  const handleTrackClick = (e: React.MouseEvent) => {
    if (!contentRef.current || !trackRef.current || !thumbRef.current) return;

    const track = trackRef.current;
    const trackRect = track.getBoundingClientRect();
    const thumbHeight = thumbRef.current.clientHeight;
    const clickPosition = e.clientY - trackRect.top;
    const clickRatio = clickPosition / track.clientHeight;
    const scrollPosition = clickRatio * contentRef.current.scrollHeight;

    contentRef.current.scrollTo({
      top: scrollPosition - thumbHeight / 2,
      behavior: 'smooth',
    });
  };

  return (
    <ScrollbarContainer className={className}>
      <ContentWrapper ref={combinedRef} onScroll={onScroll}>
        {children}
      </ContentWrapper>
      <ScrollbarTrack ref={trackRef} onClick={handleTrackClick}>
        <ScrollbarThumb ref={thumbRef} $height={thumbHeight} onMouseDown={handleThumbMouseDown} />
      </ScrollbarTrack>
    </ScrollbarContainer>
  );
};
